/**
 * PIN-based Encryption Module
 *
 * Fallback encryption for browsers that don't support WebAuthn PRF extension.
 * Uses PBKDF2 to derive AES-GCM-256 keys from user-provided PIN.
 *
 * Security considerations:
 * - 100,000 PBKDF2 iterations to slow brute-force attacks
 * - Random salt per keypair (stored alongside encrypted data)
 * - AES-GCM for authenticated encryption
 */

// PBKDF2 iterations - balance between security and UX
const PBKDF2_ITERATIONS = 100_000;

/**
 * Derive an AES-GCM-256 encryption key from a PIN using PBKDF2.
 *
 * @param pin - User-provided PIN (6+ characters recommended)
 * @param salt - Random salt (generate with generateSalt(), store alongside encrypted data)
 * @returns AES-GCM-256 CryptoKey for encryption/decryption
 */
export async function deriveKeyWithPIN(pin: string, salt: Uint8Array): Promise<CryptoKey> {
  if (!pin || pin.length < 4) {
    throw new Error('PIN must be at least 4 characters');
  }

  if (!salt || salt.length < 16) {
    throw new Error('Salt must be at least 16 bytes');
  }

  const encoder = new TextEncoder();

  // Import PIN as raw key material for PBKDF2
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(pin),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  // Derive AES-GCM key using PBKDF2
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false, // Non-extractable
    ['encrypt', 'decrypt']
  );
}

/**
 * Generate a random salt for PBKDF2.
 * Store this alongside the encrypted keypair.
 *
 * @returns 32-byte random salt
 */
export function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

/**
 * Validate PIN strength.
 * Returns validation result with feedback.
 *
 * @param pin - PIN to validate
 * @returns Validation result with isValid flag and message
 */
export function validatePIN(pin: string): { isValid: boolean; message: string } {
  if (!pin) {
    return { isValid: false, message: 'PIN is required' };
  }

  if (pin.length < 4) {
    return { isValid: false, message: 'PIN must be at least 4 characters' };
  }

  if (pin.length < 6) {
    return { isValid: true, message: 'Weak PIN - consider using 6+ characters' };
  }

  // Check for common patterns
  const isAllSame = /^(.)\1+$/.test(pin);
  if (isAllSame) {
    return { isValid: false, message: 'PIN cannot be all the same character' };
  }

  const isSequential = /^(0123|1234|2345|3456|4567|5678|6789|9876|8765|7654|6543|5432|4321|3210)/.test(
    pin
  );
  if (isSequential) {
    return { isValid: false, message: 'PIN cannot be a sequential pattern' };
  }

  return { isValid: true, message: 'PIN accepted' };
}
